import { ChevronLeftIcon, PlusIcon } from "@heroicons/react/24/outline"
import { Breadcrumb, Button, Spin, Tabs } from "antd"
import type { TabsProps, FormInstance } from "antd"
import { Button as CustomButton } from "components"
import React, { useMemo, useRef, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAppNotificationStore } from "stores"
import { useGet } from "utils/useFetch"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import type {
  IDutyLocationDoc,
  IWorkplaceByIdRes,
  IWorkplaceDetailDoc,
  IShiftPlanDoc,
  IShiftPlansRes,
  HardwareDevices,
} from "../../typings"
import { SETUP_WORKPLACE_QUERY_KEY } from "../../typings"
import calendarIcon from "assets/svg/CalendarDots.svg"
import rightArrowIcon from "assets/svg/chevron-right.svg"
import RosteredPlans from "./RosteredPlans"
import AnytimePlans from "./AnytimePlans"
import CreateShiftPlan from "./CreateShiftPlan"
import CreateShiftPlanConfiguration from "./CreateShiftPlanConfiguration"
import CreateShiftPlanPeriods from "./CreateShiftPlanPeriods"
import CreateShiftPlanTables from "./CreateShiftPlanTables"
import type { ShiftPlanBody } from "../../overview/ConfigurationWorkplace"
import SelectDutyLocationWrapper from "../../components/SelectDutyLocationWrapper"
import { WorkplaceName } from "./SetupDutyLocations"
import {
  buildShiftPlanBodyConfig,
  invalidateShiftPlanQueries,
  shiftPlanToConfigInitialValuesConfig,
} from "../../utils/shiftTablesUtils"
import { CustomBackButton } from "../Setup"
import { useNavigate, useSearchParams } from "react-router-dom"

// ─── Types ────────────────────────────────────────────────────────────────────

type DutyLocationSelection = number | "workplace"

export interface ConfigFormValues {
  allowManualTimeEntry?: boolean
  requireApprovalForManualTimeEntry?: boolean
  useInPayrollCalculation?: boolean
  allowSwapping?: boolean
  requireApprovalForSwap?: boolean
  allowLateClockIn?: boolean
  allowedLateClockinInMinutes?: string
  allowOvertime?: boolean
  useOvertimeRecord?: boolean
  linkHardwareDevice?: boolean
  hardwareDeviceTypes?: (string | HardwareDevices)[]
  hardwareSetupContactEmail?: string
  allowGeoFencing?: boolean
  geofencingOffsetMeters?: number
  addOvertimeToPayroll?: boolean
  smsNotification?: boolean
  notificationCadence?: string
  notifiyMeBefore?: boolean
}

export type ConfigInitialValues = Partial<ConfigFormValues>

export type ExtendedShiftPlanDoc = IShiftPlanDoc

interface DutyLocationOption {
  id: number | "workplace"
  name: string
  addressLine1?: string
}

interface SetupShiftPlansProps {
  onBack: () => void
  onNext: () => void
  saveToDraft: () => void
  isDrafting: boolean
  workplaceId?: number
  onTrackWorkplaceConfig?: (updates: Record<string, boolean>) => void
  isTrackingProgress?: boolean
  isSavingProgress?: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────

const SetupShiftPlans: React.FC<SetupShiftPlansProps> = ({
  onBack,
  onNext,
  workplaceId,
  onTrackWorkplaceConfig,
  isTrackingProgress = false,
  isSavingProgress = false,
  saveToDraft,
  isDrafting,
}) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const rawDutyLocationFromUrl = searchParams.get("dutylocationId")
  const selectedDutyLocationId = useMemo(() => {
    if (rawDutyLocationFromUrl === "workplace" || !rawDutyLocationFromUrl)
      return "workplace"
    return Number(rawDutyLocationFromUrl)
  }, [rawDutyLocationFromUrl])

  const [activeTab, setActiveTab] = useState("1")
  const [showCreateFlow, setShowCreateFlow] = useState(false)
  const [editingPlan, setEditingPlan] = useState<IShiftPlanDoc | null>(null)

  const configFormRef = useRef<FormInstance<ConfigFormValues>>(null)
  const { toast } = useAppNotificationStore()
  const queryClient = useQueryClient()

  // ── Editing plan detail ────────────────────────────────────────────────────

  const { data: editingPlanDetailRes } = useGet<{ data: IShiftPlanDoc }>({
    url:
      editingPlan != null
        ? `/v1/time-attendance/shift-plans/${editingPlan.id}`
        : "",
    instanceKey: "ewaAxiosInstance",
    enabled: !!editingPlan?.id,
  })

  const planForConfig: ExtendedShiftPlanDoc | null =
    editingPlanDetailRes?.data ?? editingPlan ?? null

  // ── Workplace + duty locations ─────────────────────────────────────────────

  const { data: workplaceRes, isLoading: isDutyLocationsLoading } =
    useGet<IWorkplaceByIdRes>({
      url: `/v1/time-attendance/workplaces/${workplaceId}`,
      queryKey: [SETUP_WORKPLACE_QUERY_KEY, String(workplaceId)],
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceId,
    })

  const workplace: IWorkplaceDetailDoc | undefined = workplaceRes?.data
  const dutyLocations: IDutyLocationDoc[] = workplace?.officeDutyLocations ?? []

  const primaryWorkplaceLabel =
    workplace?.displayName ?? workplace?.fullAddress ?? ""

  // ── Derived selection state ────────────────────────────────────────────────

  const effectiveSelection: DutyLocationSelection =
    selectedDutyLocationId ?? dutyLocations[0]?.id ?? "workplace"

  const currentDutyLocationId: number | null =
    effectiveSelection === "workplace" ? null : effectiveSelection

  const currentDutyLocationName =
    selectedDutyLocationId === "workplace"
      ? primaryWorkplaceLabel
      : dutyLocations.find((l) => l.id === currentDutyLocationId)?.name ?? ""

  // ── Shift plans query ──────────────────────────────────────────────────────

  const shiftPlansQueryParams = new URLSearchParams({
    dutylocationId: String(
      selectedDutyLocationId === "workplace"
        ? "null"
        : currentDutyLocationId ?? ""
    ),
    ...(workplaceId != null && { workplaceId: String(workplaceId) }),
  }).toString()

  const shiftPlansUrl = `/v1/time-attendance/shift-plans?${shiftPlansQueryParams}`

  const { data: shiftPlansRes, isFetching: isShiftPlansFetching } =
    useGet<IShiftPlansRes>({
      url: shiftPlansUrl,
      queryKey: [shiftPlansUrl],
      instanceKey: "ewaAxiosInstance",
    })

  const docs = shiftPlansRes?.data?.docs ?? []

  const rosteredPlans = useMemo(
    () => docs.filter((d): d is IShiftPlanDoc => d.type === "rostered"),
    [docs]
  )
  const flexiblePlans = useMemo(
    () => docs.filter((d): d is IShiftPlanDoc => d.type === "flexible"),
    [docs]
  )

  const invalidateShiftPlans = () => {
    queryClient.invalidateQueries({ queryKey: [shiftPlansUrl] })
    invalidateShiftPlanQueries(queryClient)
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  const { mutate: restoreShiftPlan, isLoading: isRestoring } = useMutation({
    mutationFn: async (planId: number) => {
      const res = await ewaAxiosInstance.post(
        `/v1/time-attendance/shift-plans/${planId}/restore`
      )
      return res.data
    },
    onSuccess: invalidateShiftPlans,
    onError: onQueryError,
  })

  const { mutate: toggleShiftPlan, isLoading: isToggling } = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ShiftPlanBody }) =>
      ewaAxiosInstance.put(`/v1/time-attendance/shift-plans/${id}`, body),
    onSuccess: invalidateShiftPlans,
    onError: onQueryError,
  })

  const { mutate: archiveShiftPlan, isLoading: isArchiving } = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ShiftPlanBody }) =>
      ewaAxiosInstance.post(
        `/v1/time-attendance/shift-plans/${id}/archive`,
        body
      ),
    onSuccess: invalidateShiftPlans,
    onError: onQueryError,
  })

  const { mutate: updateShiftPlan, isLoading: isUpdatingPlan } = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number
      body: Record<string, unknown>
    }) => {
      const res = await ewaAxiosInstance.put(
        `/v1/time-attendance/shift-plans/${id}`,
        body
      )
      return res.data
    },
    onSuccess: () => {
      invalidateShiftPlans()
      if (editingPlan?.id) {
        queryClient.invalidateQueries({
          queryKey: [`/v1/time-attendance/shift-plans/${editingPlan.id}`],
        })
      }
      toast.success("Shift plan updated successfully")
    },
    onError: onQueryError,
  })

  const { mutate: toDraft, isLoading: isSavingToDraft } = useMutation({
    mutationFn: async ({
      status,
      id,
    }: {
      status: "draft" | "published"
      id: number
    }) => {
      const res = await ewaAxiosInstance.put(
        `/v1/time-attendance/workplace-config/${id}`,
        { status }
      )
      return res.data
    },
    onSuccess: (data) => {
      toast.success("Draft Workplace successfully")
      console.log("Data -> ", data)
      queryClient.invalidateQueries({
        queryKey: [`/v1/time-attendance/workplaces?${workplaceId}`],
      })
      navigate("/dashboard/time-and-attendance/overview")
    },
    onError: onQueryError,
  })

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSaveConfig = async () => {
    if (!editingPlan) return
    try {
      await configFormRef.current?.validateFields()
      const config = configFormRef.current?.getFieldsValue() ?? {}
      updateShiftPlan({
        id: editingPlan.id,
        body: buildShiftPlanBodyConfig(config),
      })
    } catch {
      // Validation failed — form will display field errors
    }
  }

  const handleDutyLocationChange = (value: string | number) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set("dutylocationId", String(value))
    setSearchParams(nextParams, { replace: true })
  }

  // ── Duty location options (typed, no `as any`) ─────────────────────────────

  const dutyLocationOptions: DutyLocationOption[] = [
    ...(primaryWorkplaceLabel
      ? [
          {
            id: "workplace" as const,
            name: primaryWorkplaceLabel,
            addressLine1: workplace?.fullAddress,
          },
        ]
      : []),
    ...dutyLocations,
  ]

  // ── Tab definitions ────────────────────────────────────────────────────────

  const editViewSubTabs: TabsProps["items"] = editingPlan
    ? [
        {
          key: "1",
          label: "Permissions & rules",
          children: planForConfig ? (
            <CreateShiftPlanConfiguration
              key={`config-${editingPlan.id}-${
                editingPlanDetailRes ? "detail" : "list"
              }`}
              shiftPlanName={editingPlan.title}
              formRef={configFormRef}
              initialValues={shiftPlanToConfigInitialValuesConfig(
                planForConfig
              )}
              mode="edit"
              onSave={handleSaveConfig}
              saveLoading={isUpdatingPlan}
            />
          ) : null,
        },
        {
          key: "2",
          label: "Shift periods",
          children: (
            <CreateShiftPlanPeriods
              key={editingPlan.id}
              shiftPlanName={editingPlan.title}
              shiftPlanId={editingPlan.id}
            />
          ),
        },
        {
          key: "3",
          label: "Employee assignment",
          children: (
            <CreateShiftPlanTables
              key={editingPlan.id}
              shiftPlanName={editingPlan.title}
              shiftType={
                editingPlan.type === "flexible" ? "anytime" : "rostered"
              }
              shiftPlanId={editingPlan.id}
              dutyLocationId={
                editingPlan.dutylocationId ?? currentDutyLocationId ?? undefined
              }
              workplaceId={workplaceId!}
            />
          ),
        },
      ]
    : []

  const tabIcon = (
    <img
      width={15}
      height={15}
      src={calendarIcon}
      alt=""
    />
  )

  const tabItems: TabsProps["items"] = [
    {
      key: "1",
      label: (
        <div className="flex items-center gap-1 justify-center">
          {tabIcon}
          <span className="tab-text">Rostered plans</span>
        </div>
      ),
      children: editingPlan ? (
        <EditingPlanView
          editingPlan={editingPlan}
          editViewSubTabs={editViewSubTabs}
          onBack={() => setEditingPlan(null)}
        />
      ) : (
        <RosteredPlans
          plans={rosteredPlans}
          archiveShiftPlan={archiveShiftPlan}
          onRestorePlan={restoreShiftPlan}
          onEditPlan={setEditingPlan}
          goToCreateFlow={() => setShowCreateFlow(true)}
          toggleShiftPlan={toggleShiftPlan}
          createShift={setShowCreateFlow}
        />
      ),
    },
    {
      key: "2",
      label: (
        <div className="flex items-center gap-1 justify-center">
          {tabIcon}
          <span className="tab-text">Flexible plans</span>
        </div>
      ),
      children: editingPlan ? (
        <EditingPlanView
          editingPlan={editingPlan}
          editViewSubTabs={editViewSubTabs}
          onBack={() => setEditingPlan(null)}
        />
      ) : (
        <AnytimePlans
          plans={flexiblePlans}
          onRestorePlan={restoreShiftPlan}
          onEditPlan={setEditingPlan}
          toggleShiftPlan={toggleShiftPlan}
          archiveShiftPlan={archiveShiftPlan}
          createShift={setShowCreateFlow}
        />
      ),
    },
  ]

  // ── Create flow ────────────────────────────────────────────────────────────

  if (showCreateFlow) {
    return (
      <CreateShiftPlan
        onBack={() => setShowCreateFlow(false)}
        onComplete={() => {
          setShowCreateFlow(false)
          onNext()
        }}
        workplaceId={workplaceId}
        onTrackProgress={onTrackWorkplaceConfig}
        isTrackingProgress={isTrackingProgress}
        saveToDraft={() => toDraft({ status: "draft", id: workplaceId! })}
        isDrafting={isDrafting}
      />
    )
  }

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <>
      {/* Header (static) */}
      <div className="flex flex-col gap-4 shrink-0">
        <CustomBackButton onBack={onBack} />

        <div className="flex flex-col gap-2">
          <h5 className="text-[24px] text-[#0F172B] font-bold">Shift plans</h5>
          <p className="text-sm text-slate-500">
            Choose how you want to schedule employees for this workplace. You
            can create multiple shift plans with different types later.
          </p>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 pt-4 pr-2">
        <SelectDutyLocationWrapper
          dutyLocation={dutyLocationOptions}
          isLoadingDutyLocation={isDutyLocationsLoading}
          selectedLocation={effectiveSelection}
          setSelectedLocation={handleDutyLocationChange}
        />

        <div className="flex items-center justify-between">
          <p className="font-bold text-[16px] text-[#0F172B]">
            Shift plans for <WorkplaceName name={currentDutyLocationName} />
          </p>
          <Button
            type="default"
            className="font-semibold px-4 h-10 rounded-md border-2 border-[#004AF5] text-[#004AF5] bg-white hover:!border-[#004AF5] hover:!text-[#004AF5] hover:!bg-[#F8FAFC] flex items-center gap-2"
            onClick={() => setShowCreateFlow(true)}
          >
            <PlusIcon className="w-4 h-4" />
            Create shift plan
          </Button>
        </div>

        <Spin spinning={isShiftPlansFetching || isArchiving || isRestoring}>
          <Tabs
            defaultActiveKey={activeTab}
            className="[&_.tab-text]:text-slate-500 [&_.ant-tabs-tab-active_.tab-text]:!text-slate-900 [&_.ant-tabs-ink-bar]:!bg-slate-900"
            items={tabItems}
            onChange={setActiveTab}
          />
        </Spin>
      </div>

      <div className="shrink-0 flex justify-end items-center gap-3 pt-4 border-t border-[#E2E8F0]">
        <Button
          type="default"
          className="font-semibold px-6 h-10 rounded-md border-2 border-[#E2E8F0] text-[#0F172B]"
          onClick={saveToDraft}
          loading={isDrafting}
        >
          Save to drafts
        </Button>
        <CustomButton
          color="primary"
          className="h-10"
          onClick={onNext}
          submitting={isSavingProgress}
          disabled={
            isSavingProgress ||
            isShiftPlansFetching ||
            shiftPlansRes?.data?.docs?.length === 0 ||
            isDrafting
          }
        >
          Next step
        </CustomButton>
      </div>
    </>
  )
}

export default SetupShiftPlans

// ─── Sub-components ───────────────────────────────────────────────────────────

interface EditingPlanViewProps {
  editingPlan: IShiftPlanDoc
  editViewSubTabs: TabsProps["items"]
  onBack: () => void
}

const EditingPlanView: React.FC<EditingPlanViewProps> = ({
  editingPlan,
  editViewSubTabs,
  onBack,
}) => (
  <div className="flex flex-col gap-3 pt-2">
    <Breadcrumb
      separator={
        <img
          src={rightArrowIcon}
          width={15}
          height={15}
          alt=""
        />
      }
      items={[
        {
          title: (
            <span
              className="text-slate-500 hover:underline cursor-pointer"
              onClick={onBack}
            >
              Shift plans
            </span>
          ),
        },
        {
          title: <span className="text-[#0F172B]">{editingPlan.title}</span>,
        },
      ]}
    />
    <h5 className="text-[24px] text-[#0F172B] font-bold">
      {editingPlan.title}
    </h5>
    <Tabs
      defaultActiveKey="1"
      items={editViewSubTabs}
      className="[&_.ant-tabs-ink-bar]:hidden [&_.ant-tabs-nav]:bg-slate-100 [&_.ant-tabs-tab-btn]:text-black [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-black [&_.ant-tabs-nav]:py-1 [&_.ant-tabs-nav]:px-1.5 [&_.ant-tabs-nav]:rounded-full [&_.ant-tabs-nav]:inline-flex [&_.ant-tabs-nav]:!w-max [&_.ant-tabs-tab]:flex-none [&_.ant-tabs-tab]:px-3 [&_.ant-tabs-tab]:py-2 [&_.ant-tabs-tab]:rounded-full [&_.ant-tabs-tab]:transition [&_.ant-tabs-tab-active]:bg-white [&_.ant-tabs-tab-active]:font-semibold"
    />
  </div>
)
